import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { 
  Heart, 
  DollarSign, 
  Building, 
  GraduationCap, 
  Palette,
  Users,
  BarChart3,
  Package,
  Megaphone,
  FolderKanban,
  CheckCircle,
  ArrowRight,
  Star,
  Play,
  Shield,
  Globe,
  Cloud,
  Database,
  Workflow,
  Brain,
  Target,
  Award
} from 'lucide-react';
import { YOUTUBE_CHANNEL, PLATFORM_VIDEOS, PRODUCT_TUTORIAL_VIDEOS, createYouTubeEmbedUrl, handleMissingVideo } from '../config/videos';

const Products = () => {
  const mainProducts = [
    {
      name: 'AquaCare',
      tagline: 'Healthcare Management Excellence',
      description: 'Comprehensive healthcare automation platform designed for hospitals, clinics, and healthcare providers.',
      icon: Heart,
      color: 'from-red-500 to-pink-500',
      category: 'Healthcare',
      image: '/images/Modern_hospital_setting_with_healthcare_profession_1750595898.png',
      features: [
        'Patient Management System',
        'Electronic Health Records (EHR)',
        'Appointment Scheduling & Reminders',
        'Billing & Insurance Processing',
        'Prescription Management',
        'Medical Inventory Tracking',
        'Telemedicine Integration',
        'HIPAA Compliance & Security'
      ],
      metrics: {
        efficiency: '75% faster patient processing',
        cost: '60% reduction in admin costs',
        satisfaction: '95% patient satisfaction'
      },
      industries: ['Hospitals', 'Clinics', 'Dental Practices', 'Specialty Care']
    },
    {
      name: 'AquaInvoice',
      tagline: 'Financial Operations Platform',
      description: 'Advanced financial management and inventory control system for modern businesses.',
      icon: DollarSign,
      color: 'from-green-500 to-emerald-500',
      category: 'Finance',
      image: '/images/Professional_accountant_working_with_automated_inv_1750595930.png',
      features: [
        'Automated Invoice Generation',
        'Real-time Inventory Management',
        'Financial Reporting & Analytics',
        'Multi-currency Support',
        'Tax Compliance & Automation',
        'Vendor Management',
        'Purchase Order Processing',
        'Cash Flow Forecasting'
      ],
      metrics: {
        efficiency: '80% faster invoice processing',
        accuracy: '99.9% financial accuracy',
        cost: '50% reduction in processing costs'
      },
      industries: ['Retail', 'Manufacturing', 'Distribution', 'E-commerce']
    },
    {
      name: 'AquaBizz',
      tagline: 'Enterprise Business Intelligence',
      description: 'Complete business management platform with advanced analytics and automation capabilities.',
      icon: Building,
      color: 'from-blue-500 to-cyan-500',
      category: 'Business Intelligence',
      image: '/images/Executive_boardroom_with_large_wall_displays_showi_1750595962.png',
      features: [
        'Business Process Automation',
        'Advanced Analytics & BI',
        'Performance Dashboards',
        'Workflow Management',
        'Document Management',
        'Team Collaboration Tools',
        'Project Management',
        'Compliance Monitoring'
      ],
      metrics: {
        productivity: '65% increase in productivity',
        decisions: '3x faster decision making',
        roi: '300% ROI within 12 months'
      },
      industries: ['Enterprises', 'SMBs', 'Consulting', 'Professional Services']
    },
    {
      name: 'AquaLearn',
      tagline: 'Learning Management System',
      description: 'Comprehensive educational platform for corporate training and academic institutions.',
      icon: GraduationCap,
      color: 'from-purple-500 to-indigo-500',
      category: 'Education',
      image: '/images/Modern_classroom_or_training_facility_with_digital_1750595993.png',
      features: [
        'Course Management System',
        'Virtual Classroom Integration',
        'Assessment & Certification',
        'Progress Tracking & Analytics',
        'Mobile Learning Support',
        'Content Management',
        'Student Information System',
        'Integration with HR Systems'
      ],
      metrics: {
        engagement: '85% learner engagement',
        completion: '92% course completion rate',
        efficiency: '70% training cost reduction'
      },
      industries: ['Corporations', 'Universities', 'Training Centers', 'Government']
    },
    {
      name: 'AquaStyle',
      tagline: 'Creative Project Management',
      description: 'Specialized platform for creative agencies, design studios, and marketing teams.',
      icon: Palette,
      color: 'from-pink-500 to-rose-500',
      category: 'Creative',
      image: '/images/Design_studio_environment_with_creative_profession_1750596025.png',
      features: [
        'Project Portfolio Management',
        'Creative Asset Management',
        'Client Collaboration Tools',
        'Time Tracking & Billing',
        'Brand Guidelines Management',
        'Approval Workflows',
        'Resource Planning',
        'Creative Analytics'
      ],
      metrics: {
        delivery: '40% faster project delivery',
        quality: '90% first-time approval rate',
        utilization: '85% resource utilization'
      },
      industries: ['Agencies', 'Design Studios', 'Marketing Teams', 'Media Companies']
    }
  ];

  const customSolutions = [
    {
      name: 'AquaCRM',
      description: 'Enterprise customer relationship management with AI-powered insights and automation.',
      icon: Users,
      features: ['360° Customer View', 'Sales Pipeline Management', 'Marketing Automation', 'Service Integration']
    },
    {
      name: 'AquaHR',
      description: 'Complete human resources management system with talent acquisition and performance tracking.',
      icon: BarChart3,
      features: ['Talent Management', 'Performance Analytics', 'Payroll Integration', 'Compliance Tracking']
    },
    {
      name: 'AquaInventory',
      description: 'Advanced inventory and supply chain management with predictive analytics.',
      icon: Package,
      features: ['Real-time Tracking', 'Demand Forecasting', 'Supplier Management', 'Quality Control']
    },
    {
      name: 'AquaMarketing',
      description: 'Comprehensive marketing automation platform with multi-channel campaign management.',
      icon: Megaphone,
      features: ['Campaign Management', 'Lead Scoring', 'Social Media Integration', 'ROI Analytics']
    },
    {
      name: 'AquaProject',
      description: 'Enterprise project management with resource optimization and collaboration tools.',
      icon: FolderKanban,
      features: ['Resource Planning', 'Gantt Charts', 'Team Collaboration', 'Budget Management']
    }
  ];

  const platformFeatures = [
    {
      icon: Brain,
      title: 'AI-Powered Automation',
      description: 'Leverage artificial intelligence to automate complex business processes and decision-making.'
    },
    {
      icon: Cloud,
      title: 'Cloud-Native Architecture',
      description: 'Built on enterprise-grade cloud infrastructure for scalability, security, and reliability.'
    },
    {
      icon: Shield,
      title: 'Enterprise Security',
      description: 'Bank-grade security with advanced threat protection and enterprise compliance.'
    },
    {
      icon: Database,
      title: 'Unified Data Platform',
      description: 'Connect all your data sources in real-time with advanced analytics and reporting capabilities.'
    },
    {
      icon: Workflow,
      title: 'No-Code Development',
      description: 'Build and customize applications without coding using our intuitive drag-and-drop interface.'
    },
    {
      icon: Globe,
      title: 'Global Scalability',
      description: 'Deploy globally with multi-language, multi-currency, and multi-timezone support.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section - Enterprise Focus */}
      <section className="relative bg-gradient-to-br from-primary/10 via-white to-secondary/5 py-20 lg:py-32">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-secondary/5 opacity-40"></div>
        </div>
        
        <div className="container mx-auto px-6 relative z-10">
          <div className="text-center max-w-5xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
            >
              {/* Enterprise Badge */}
              <div className="inline-flex items-center bg-gradient-to-r from-primary/10 to-secondary/10 rounded-full px-6 py-2 mb-6">
                <Award className="h-4 w-4 text-primary mr-2" />
                <span className="text-sm font-semibold text-gray-700">Core Platform + Off-the-Shelf Solutions</span>
              </div>

              <h1 className="text-4xl lg:text-6xl xl:text-7xl font-bold text-gray-900 leading-tight mb-6">
                Enterprise Automation
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-primary-500 to-secondary-500">
                  Platform & Solutions
                </span>
              </h1>
              
              <p className="text-xl lg:text-2xl text-gray-600 mb-10 leading-relaxed">
                Our core enterprise automation platform enables you to build any business application. 
                Plus, choose from our pre-built industry solutions to accelerate deployment.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Explore All Products
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.a
                  href={YOUTUBE_CHANNEL.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-primary hover:text-primary transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Product Demo
                </motion.a>
              </div>

              {/* Trust Indicators */}
              <div className="mt-12 pt-8 border-t border-gray-200">
                <div className="flex flex-wrap items-center justify-center gap-8 opacity-70">
                  <div className="flex items-center">
                    <Star className="h-5 w-5 text-yellow-500 mr-1" />
                    <span className="text-sm font-medium">4.9/5 Enterprise Rating</span>
                  </div>
                  <div className="flex items-center">
                    <Shield className="h-5 w-5 text-green-500 mr-1" />
                    <span className="text-sm font-medium">Enterprise Security</span>
                  </div>
                  <div className="flex items-center">
                    <Users className="h-5 w-5 text-blue-500 mr-1" />
                    <span className="text-sm font-medium">Enterprise Clients Worldwide</span>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Platform Section */}
      <section id="core-platform" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              🚀 Our Core Platform
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              The flagship enterprise automation platform that powers everything we do. 
              Build any business application, automate any workflow, with complete onboarding and training included.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="bg-white rounded-2xl p-10 shadow-xl border border-gray-200"
            >
              <div className="text-center mb-8">
                <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center">
                  <Building className="h-10 w-10 text-white" />
                </div>
                <h3 className="text-3xl font-bold text-gray-900 mb-4">Enterprise Business Automation Platform</h3>
                <p className="text-lg text-gray-600 leading-relaxed">
                  The only platform you need to digitize, automate, and optimize your entire business. 
                  From simple workflows to complex enterprise systems - we build it for you.
                </p>
              </div>

              <div className="grid md:grid-cols-2 gap-8 mb-8">
                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">What We Build For You:</h4>
                  <div className="space-y-3">
                    {[
                      'Custom CRM & Sales Systems',
                      'ERP & Financial Management',
                      'HR & Employee Management',
                      'Project & Workflow Management',
                      'Inventory & Supply Chain',
                      'Customer Support Systems',
                      'Analytics & Reporting Dashboards',
                      'API Integrations & Automations'
                    ].map((feature, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h4 className="text-xl font-bold text-gray-900 mb-4">Complete Service Package:</h4>
                  <div className="space-y-3">
                    {[
                      'Requirements Analysis & Planning',
                      'Custom Application Development',
                      'Data Migration & Integration',
                      'User Training & Onboarding',
                      'Ongoing Support & Maintenance',
                      'Performance Optimization',
                      'Security & Compliance Setup',
                      'Dedicated Success Manager'
                    ].map((service, index) => (
                      <div key={index} className="flex items-center">
                        <CheckCircle className="h-5 w-5 text-primary mr-3 flex-shrink-0" />
                        <span className="text-gray-700">{service}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <motion.button
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-8 py-4 rounded-lg font-semibold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
                >
                  Start Your Automation Journey
                  <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
                </motion.button>
                
                <motion.a
                  href={YOUTUBE_CHANNEL.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                  className="border-2 border-gray-300 text-gray-700 px-8 py-4 rounded-lg font-semibold text-lg hover:border-primary hover:text-primary transition-colors duration-300 flex items-center justify-center group"
                >
                  <Play className="mr-2 h-5 w-5" />
                  Watch Platform Demo
                </motion.a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Off-the-Shelf Solutions */}
      <section id="off-the-shelf-solutions" className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              📦 Off-the-Shelf Industry Solutions
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Pre-built industry applications powered by our core platform. 
              Quick deployment for common business functions while you focus on your unique needs.
            </p>
          </div>

          <div className="space-y-20">
            {mainProducts.map((product, index) => (
              <motion.div
                key={product.name}
                id={product.name.toLowerCase().replace(/\s+/g, '').replace('aqua', 'aqua')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`grid lg:grid-cols-2 gap-12 items-center ${index % 2 === 1 ? 'lg:grid-flow-col-dense' : ''}`}
              >
                <div className={`space-y-6 ${index % 2 === 1 ? 'lg:col-start-2' : ''}`}>
                  <div className="flex items-center space-x-4">
                    <div className={`w-16 h-16 rounded-xl bg-gradient-to-r ${product.color} flex items-center justify-center`}>
                      <product.icon className="h-8 w-8 text-white" />
                    </div>
                    <div>
                      <h3 className="text-3xl font-bold text-gray-900">{product.name}</h3>
                      <p className="text-lg text-primary font-semibold">{product.tagline}</p>
                    </div>
                  </div>

                  <p className="text-lg text-gray-700 leading-relaxed">{product.description}</p>

                  {/* Key Metrics */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    {Object.entries(product.metrics).map(([key, value]) => (
                      <div key={key} className="bg-gray-50 p-4 rounded-lg text-center">
                        <div className="text-2xl font-bold text-primary mb-1">{value}</div>
                        <div className="text-sm text-gray-600 capitalize">{key}</div>
                      </div>
                    ))}
                  </div>

                  {/* Features Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {product.features.slice(0, 6).map((feature, idx) => (
                      <div key={idx} className="flex items-center space-x-2">
                        <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Industry Tags */}
                  <div className="flex flex-wrap gap-2">
                    {product.industries.map((industry, idx) => (
                      <span key={idx} className="bg-primary/10 text-primary px-3 py-1 rounded-full text-sm font-medium">
                        {industry}
                      </span>
                    ))}
                  </div>

                  <div className="flex space-x-4">
                    <Link 
                      to={`/products/${product.name.toLowerCase().replace(/\s+/g, '')}`}
                      className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center group"
                    >
                      Learn More
                      <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                    </Link>
                    <button className="border-2 border-gray-300 text-gray-700 px-6 py-3 rounded-lg font-semibold hover:border-primary hover:text-primary transition-colors duration-300">
                      Try Demo
                    </button>
                  </div>
                </div>

                <div className={`relative ${index % 2 === 1 ? 'lg:col-start-1' : ''}`}>
                  <div className="relative">
                    <img
                      src={product.image}
                      alt={`${product.name} Platform Interface`}
                      className="w-full h-auto rounded-2xl shadow-2xl border border-gray-100"
                    />
                    <div className="absolute top-4 right-4 flex items-center space-x-2 bg-white/90 backdrop-blur-sm px-3 py-2 rounded-lg">
                      <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                      <span className="text-sm text-green-600 font-medium">Live</span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Custom Enterprise Solutions */}
      <section id="custom-solutions" className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Custom Enterprise Solutions
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Tailored solutions built on our platform to address specific enterprise needs and industry requirements.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {customSolutions.map((solution, index) => (
              <motion.div
                key={solution.name}
                id={solution.name.toLowerCase().replace(/\s+/g, '').replace('aqua', 'aqua')}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 group"
              >
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform">
                  <solution.icon className="h-8 w-8 text-primary" />
                </div>
                
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{solution.name}</h3>
                <p className="text-gray-600 mb-6 leading-relaxed">{solution.description}</p>
                
                <div className="space-y-2 mb-6">
                  {solution.features.map((feature, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-500 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <Link 
                  to={`/solutions/${solution.name.toLowerCase().replace(/\s+/g, '')}`}
                  className="w-full bg-gradient-to-r from-primary-500 to-secondary-500 text-white py-3 rounded-lg font-semibold hover:shadow-lg transition-all duration-300 flex items-center justify-center group"
                >
                  Learn More
                  <ArrowRight className="ml-2 h-4 w-4 group-hover:translate-x-1 transition-transform" />
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Composable Architecture Section */}
      <section className="py-20 bg-gradient-to-br from-gray-50 to-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Composable Business Architecture
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-4xl mx-auto mb-8">
              Our platform enables composable enterprise architecture through modular, API-first design. 
              Build with packaged business capabilities (PBCs) that can be assembled, reassembled, and scaled independently.
            </p>
            
            <div className="bg-white rounded-2xl p-8 shadow-lg border max-w-4xl mx-auto">
              <div className="grid md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="bg-gradient-to-r from-primary/10 to-primary/5 w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Package className="h-8 w-8 text-primary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Packaged Business Capabilities</h3>
                  <p className="text-gray-600">Pre-built, reusable components that represent specific business functions and can be combined to create custom solutions.</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-gradient-to-r from-secondary/10 to-secondary/5 w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Workflow className="h-8 w-8 text-secondary" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">API-First Design</h3>
                  <p className="text-gray-600">Every component exposes standardized APIs, enabling seamless integration and orchestration across your enterprise ecosystem.</p>
                </div>
                
                <div className="text-center">
                  <div className="bg-gradient-to-r from-accent/10 to-accent/5 w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-4">
                    <Target className="h-8 w-8 text-accent" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 mb-2">Modular Scalability</h3>
                  <p className="text-gray-600">Scale individual components independently, adapt to changing requirements, and achieve 30% faster innovation cycles.</p>
                </div>
              </div>
              
              <div className="mt-8 pt-8 border-t border-gray-200">
                <div className="text-center">
                  <p className="text-sm text-gray-500 mb-4">Composable enterprises report 30% higher revenue growth</p>
                  <div className="flex justify-center space-x-8 text-sm">
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span>Faster Time-to-Market</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span>Reduced Technical Debt</span>
                    </div>
                    <div className="flex items-center">
                      <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                      <span>Enhanced Agility</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Platform Features */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Built on Enterprise-Grade Platform
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              All our solutions are built on a unified platform that provides enterprise-grade security, 
              scalability, and performance for mission-critical business operations.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {platformFeatures.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center p-8 rounded-xl hover:bg-gray-50 transition-colors duration-300"
              >
                <div className="bg-gradient-to-r from-primary/10 to-secondary/10 w-16 h-16 rounded-lg flex items-center justify-center mx-auto mb-6">
                  <feature.icon className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{feature.title}</h3>
                <p className="text-gray-600 leading-relaxed">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Video Tutorials Section */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6"
            >
              Product Tutorials & Training
            </motion.h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive video tutorials and training materials to help you master our enterprise solutions.
            </p>
          </div>

          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Getting Started */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative group cursor-pointer"
              onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
            >
              <div className="relative bg-gradient-to-br from-primary/10 to-secondary/10 rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={createYouTubeEmbedUrl(PLATFORM_VIDEOS.gettingStarted.id)}
                  title={PLATFORM_VIDEOS.gettingStarted.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-sm font-medium text-gray-800">Getting Started</span>
                </div>
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{PLATFORM_VIDEOS.gettingStarted.title}</h3>
                <p className="text-gray-600">{PLATFORM_VIDEOS.gettingStarted.description}</p>
              </div>
            </motion.div>

            {/* Advanced Features */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="relative group cursor-pointer"
              onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
            >
              <div className="relative bg-gradient-to-br from-secondary/10 to-accent/10 rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src={createYouTubeEmbedUrl(PLATFORM_VIDEOS.advancedFeatures.id)}
                  title={PLATFORM_VIDEOS.advancedFeatures.title}
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full rounded-xl"
                />
                <div className="absolute top-4 left-4 bg-white/90 backdrop-blur-sm px-3 py-1 rounded-full">
                  <span className="text-sm font-medium text-gray-800">Advanced Training</span>
                </div>
              </div>
              <div className="mt-4">
                <h3 className="text-xl font-bold text-gray-900 mb-2">{PLATFORM_VIDEOS.advancedFeatures.title}</h3>
                <p className="text-gray-600">{PLATFORM_VIDEOS.advancedFeatures.description}</p>
              </div>
            </motion.div>
          </div>

          {/* Product-Specific Tutorials */}
          <div className="grid lg:grid-cols-4 gap-6">
            {Object.values(PRODUCT_TUTORIAL_VIDEOS).map((tutorial, index) => (
              <motion.div
                key={tutorial.product}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="bg-white rounded-lg p-4 shadow-md hover:shadow-lg transition-shadow group cursor-pointer"
                onClick={() => window.open(YOUTUBE_CHANNEL.url, '_blank')}
              >
                <div className="relative bg-gray-100 rounded-lg overflow-hidden mb-3" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    src={createYouTubeEmbedUrl(tutorial.id)}
                    title={`${tutorial.product} Tutorial`}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    className="absolute top-0 left-0 w-full h-full rounded-lg"
                  />
                  <div className="absolute top-2 left-2 bg-primary text-white px-2 py-1 rounded text-xs">
                    {tutorial.category}
                  </div>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">{tutorial.product} Tutorial</h4>
                <p className="text-sm text-gray-600">{tutorial.views} views</p>
              </motion.div>
            ))}
          </div>

          {/* YouTube Channel CTA */}
          <div className="text-center mt-12">
            <motion.a
              href={YOUTUBE_CHANNEL.url}
              target="_blank"
              rel="noopener noreferrer"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center bg-red-50 text-red-700 px-6 py-3 rounded-full hover:bg-red-100 transition-colors cursor-pointer"
            >
              <Play className="h-5 w-5 mr-2" />
              <span className="font-semibold">View All Tutorials on {YOUTUBE_CHANNEL.handle}</span>
              <ArrowRight className="h-4 w-4 ml-2" />
            </motion.a>
          </div>
        </div>
      </section>

      {/* Enterprise CTA */}
      <section className="py-20 bg-gradient-to-r from-primary-600 to-secondary-600">
        <div className="container mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="max-w-4xl mx-auto"
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Ready to Transform Your Enterprise?
            </h2>
            <p className="text-xl text-white/90 mb-10">
              Choose from our comprehensive suite of enterprise solutions or let us build a custom 
              platform tailored to your specific business requirements.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="bg-white text-primary px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center group"
              >
                Start Free Enterprise Trial
                <ArrowRight className="ml-2 h-5 w-5 group-hover:translate-x-1 transition-transform" />
              </motion.button>
              
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border-2 border-white text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-white hover:text-primary transition-colors duration-300 flex items-center justify-center group"
              >
                <Target className="mr-2 h-5 w-5" />
                Request Custom Solution
              </motion.button>
            </div>

            <div className="mt-8 text-white/80 text-sm">
              <p>✓ 30-day free trial  ✓ No setup fees  ✓ Dedicated success manager  ✓ Enterprise SLA</p>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Products; 